import React, { useState, useEffect } from 'react';
import { 
  Container,
  TextField,
  Typography,
  Grid,
  FormControl,
  Paper,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@material-ui/core';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import { api } from '@services/api';
import { useAuth } from '@context/auth';
import { Upload } from '@components/Upload';
import { FileList } from '@components/FileList';
import { useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';

interface IRisco {
  id_risco: string;
  nome: string;
}

interface IExame {
  id_exames: string;
  nome: string;
  valor: number;
}

interface IServico {
  id_servicos: string;
  nome: string;
  valor: number;
}

interface IExameToxico {
  id_exame_tox: string;
  nome: string;
  valor: number;
}

interface IContratoCredenciado {
  id_contrato: string;
  nome_contrato: string;
  cod_contrato: string;
  data_criacao: string;
  data_renovacao: string;
  arquivos?: string[];
  exames?: IExame[];
  riscos?: IRisco[];
  servicos?: IServico[];
  exames_toxicos?: IExameToxico[];
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#f9f9f9'
  },
  highlight: {
    borderLeft: '4px solid #0D9C5B',
    backgroundColor: '#f0f7f4',
    transition: 'all 0.3s ease',
    transform: 'scale(1.02)',
    boxShadow: '0 4px 20px 0 rgba(13, 156, 91, 0.2)'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh'
  },
  section: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    borderLeft: '4px solid #2196F3',
    backgroundColor: '#f0f7ff'
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    borderTop: '1px solid #e0e0e0'
  },
  dialogContent: {
    minHeight: '200px',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  }
}));

const Alert = (props: AlertProps) => {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
};

export function EditarContratoCredenciado() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();
  const contratoData = location.state as IContratoCredenciado;

  // Estados do formulário
  const [contrato, setContrato] = useState<IContratoCredenciado>(contratoData);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  // Estados para listas de opções
  const [listRiscos, setListRiscos] = useState<IRisco[]>([]);
  const [listExames, setListExames] = useState<IExame[]>([]);
  const [listServicos, setListServicos] = useState<IServico[]>([]);
  const [listExamesToxico, setListExamesToxico] = useState<IExameToxico[]>([]);

  // Estados para diálogos
  const [dialogExame, setDialogExame] = useState(false);
  const [dialogServico, setDialogServico] = useState(false);
  const [dialogToxico, setDialogToxico] = useState(false);
  const [selectedExame, setSelectedExame] = useState<IExame | null>(null);
  const [selectedServico, setSelectedServico] = useState<IServico | null>(null);
  const [selectedExameToxico, setSelectedExameToxico] = useState<IExameToxico | null>(null);
  const [valor, setValor] = useState<number | null>(null);

  // Estados para feedback
  const [snack, setSnack] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  // Estados para loading e controle de dados carregados
  const [loading, setLoading] = useState({
    inicial: true,
    enviando: false,
    riscos: false,
    exames: false,
    servicos: false,
    toxicologicos: false
  });

  const [dadosCarregados, setDadosCarregados] = useState({
    riscos: false,
    exames: false,
    servicos: false,
    toxicologicos: false
  });

  // Estados para itens destacados
  const [examesDestacados, setExamesDestacados] = useState<string[]>([]);
  const [servicosDestacados, setServicosDestacados] = useState<string[]>([]);
  const [examesToxicosDestacados, setExamesToxicosDestacados] = useState<string[]>([]);
  const [riscosDestacados, setRiscosDestacados] = useState<string[]>([]);

  // Funções para filtrar itens disponíveis
  const getExamesDisponiveis = () => {
    const nomesExamesAdicionados = contrato.exames?.map(exame => exame.nome) || [];
    return listExames.filter(exame => 
      !nomesExamesAdicionados.includes(exame.nome)
    );
  };

  const getServicosDisponiveis = () => {
    const nomesServicosAdicionados = contrato.servicos?.map(servico => servico.nome) || [];
    return listServicos.filter(servico => 
      !nomesServicosAdicionados.includes(servico.nome)
    );
  };

  const getExamesToxicosDisponiveis = () => {
    const nomesExamesToxicosAdicionados = contrato.exames_toxicos?.map(exame => exame.nome) || [];
    return listExamesToxico.filter(exame => 
      !nomesExamesToxicosAdicionados.includes(exame.nome)
    );
  };

  // Carregar dados básicos do contrato
  useEffect(() => {
    const fetchContratoData = async () => {
      try {
        setLoading((prev) => ({ ...prev, inicial: true }));

        const res = await api.post(`/ravim/dados/${contratoData.cod_contrato}`);
        const data = res.data;
        console.log('estou aq', data)
        setContrato({
          ...data,
          nome_contrato: data.nome_contrato || contratoData.nome_contrato,
          cod_contrato: data.cod_contrato || contratoData.cod_contrato,
          data_criacao: data.data_criacao || contratoData.data_criacao,
          data_renovacao: data.data_renovacao || contratoData.data_renovacao,
          exames: data.exames?.map((exame: { valor: any; }) => ({
            ...exame,
            valor: Number(exame.valor)
          })) || [],
          riscos: data.riscos || [],
          servicos: data.servicos?.map((servico: { valor: any; }) => ({
            ...servico,
            valor: Number(servico.valor)
          })) || [],
          exames_toxicos: data.exame_toxi?.map((exame: { valor: any; }) => ({
            ...exame,
            valor: Number(exame.valor)
          })) || [],
          arquivos: data.arquivos || []
        });

      } catch (error) {
        setSnack(true);
        setSeverity('error');
        setMessage('Erro ao carregar dados do contrato');
      } finally {
        setLoading((prev) => ({ ...prev, inicial: false }));
      }
    };

    fetchContratoData();
  }, [contratoData.cod_contrato]);

  // Funções para carregar dados sob demanda
  const carregarRiscos = async () => {
    if (dadosCarregados.riscos) return;

    try {
      setLoading((prev) => ({ ...prev, riscos: true }));
      const res = await api.get('/ravim/riscos');
      setListRiscos(res.data);
      setDadosCarregados((prev) => ({ ...prev, riscos: true }));
    } catch (error) {
      setSnack(true);
      setSeverity('error');
      setMessage('Erro ao carregar riscos');
    } finally {
      setLoading((prev) => ({ ...prev, riscos: false }));
    }
  };

  const carregarExames = async () => {
    if (dadosCarregados.exames) return;

    try {
      setLoading((prev) => ({ ...prev, exames: true }));
      const res = await api.get('/ravim/exames');
      setListExames(res.data);
      setDadosCarregados((prev) => ({ ...prev, exames: true }));
    } catch (error) {
      setSnack(true);
      setSeverity('error');
      setMessage('Erro ao carregar exames');
    } finally {
      setLoading((prev) => ({ ...prev, exames: false }));
    }
  };

  const carregarServicos = async () => {
    if (dadosCarregados.servicos) return;

    try {
      setLoading((prev) => ({ ...prev, servicos: true }));
      const res = await api.get('/ravim/servicos');
      setListServicos(res.data);
      setDadosCarregados((prev) => ({ ...prev, servicos: true }));
    } catch (error) {
      setSnack(true);
      setSeverity('error');
      setMessage('Erro ao carregar serviços');
    } finally {
      setLoading((prev) => ({ ...prev, servicos: false }));
    }
  };

  const carregarToxicologicos = async () => {
    if (dadosCarregados.toxicologicos) return;

    try {
      setLoading((prev) => ({ ...prev, toxicologicos: true }));
      const res = await api.get('/ravim/exame-tox');
      setListExamesToxico(res.data);
      setDadosCarregados((prev) => ({ ...prev, toxicologicos: true }));
    } catch (error) {
      setSnack(true);
      setSeverity('error');
      setMessage('Erro ao carregar exames toxicológicos');
    } finally {
      setLoading((prev) => ({ ...prev, toxicologicos: false }));
    }
  };

  // Manipuladores de diálogos
  const abrirDialogExame = () => {
    carregarExames();
    setDialogExame(true);
  };

  const abrirDialogServico = () => {
    carregarServicos();
    setDialogServico(true);
  };

  const abrirDialogToxico = () => {
    carregarToxicologicos();
    setDialogToxico(true);
  };

  // Funções para adicionar itens
  const adicionarExame = () => {
    if (!selectedExame || valor === null) return;

    const novoExame = {
      ...selectedExame,
      valor: valor
    };

    setContrato((prev) => ({
      ...prev,
      exames: [...(prev.exames || []), novoExame]
    }));

    setExamesDestacados([...examesDestacados, selectedExame.id_exames]);
    setTimeout(() => {
      setExamesDestacados(
        examesDestacados.filter((id) => id !== selectedExame.id_exames)
      );
    }, 3000);

    setSelectedExame(null);
    setValor(null);
    setDialogExame(false);
  };

  const adicionarServico = () => {
    if (!selectedServico || valor === null) return;

    const novoServico = {
      ...selectedServico,
      valor: valor
    };

    setContrato((prev) => ({
      ...prev,
      servicos: [...(prev.servicos || []), novoServico]
    }));

    setServicosDestacados([...servicosDestacados, selectedServico.id_servicos]);
    setTimeout(() => {
      setServicosDestacados(
        servicosDestacados.filter((id) => id !== selectedServico.id_servicos)
      );
    }, 3000);

    setSelectedServico(null);
    setValor(null);
    setDialogServico(false);
  };

  const adicionarExameToxico = () => {
    if (!selectedExameToxico || valor === null) return;

    const novoExameToxico = {
      ...selectedExameToxico,
      valor: valor
    };

    setContrato((prev) => ({
      ...prev,
      exames_toxicos: [...(prev.exames_toxicos || []), novoExameToxico]
    }));

    setExamesToxicosDestacados([
      ...examesToxicosDestacados,
      selectedExameToxico.id_exame_tox
    ]);
    setTimeout(() => {
      setExamesToxicosDestacados(
        examesToxicosDestacados.filter(
          (id) => id !== selectedExameToxico.id_exame_tox
        )
      );
    }, 3000);

    setSelectedExameToxico(null);
    setValor(null);
    setDialogToxico(false);
  };

  // Funções para remover itens
  const removerExame = (nome: string) => {
    setContrato((prev) => ({
      ...prev,
      exames: prev.exames?.filter((exame) => exame.nome !== nome) || []
    }));
  };

  const removerServico = (nome: string) => {
    setContrato((prev) => ({
      ...prev,
      servicos: prev.servicos?.filter((servico) => servico.nome !== nome) || []
    }));
  };

  const removerExameToxico = (nome: string) => {
    setContrato((prev) => ({
      ...prev,
      exames_toxicos: prev.exames_toxicos?.filter((exame) => exame.nome !== nome) || []
    }));
  };

  const removerRisco = (nome: string) => {
    setContrato((prev) => ({
      ...prev,
      riscos: prev.riscos?.filter((risco) => risco.nome !== nome) || []
    }));
  };

  // Manipulador de riscos
  const handleRiscosChange = (
    event: any,
    newValue: IRisco[],
    reason: string,
    detail?: any
  ) => {
    if (reason === 'select-option' && detail?.option) {
      const novoRisco = detail.option;
      setRiscosDestacados([...riscosDestacados, novoRisco.id_risco]);
      setTimeout(() => {
        setRiscosDestacados(
          riscosDestacados.filter((id) => id !== novoRisco.id_risco)
        );
      }, 3000);
    }

    setContrato((prev) => ({
      ...prev,
      riscos: newValue
    }));
  };

  // Salvar contrato
  const salvarContrato = async () => {
    try {
      setLoading((prev) => ({ ...prev, enviando: true }));

      const dadosParaEnviar = {
        nome_contrato: contrato.nome_contrato,
        cod_contrato: contrato.cod_contrato,
        data_criacao: contrato.data_criacao,
        data_renovacao: contrato.data_renovacao,
        arquivos: uploadedFiles.map((file) => file.name),
        usuario: `${user?.nome} - ${new Date().toLocaleString()}`,
        exames: contrato.exames?.map(exame => ({
          id_exame_contr_credenciado: contrato.cod_contrato,
          nome: exame.nome,
          valor: Number(exame.valor)
        })) || [],
        riscos: contrato.riscos?.map(risco => ({
          id_risco_contr_credenciado: contrato.cod_contrato,
          nome: risco.nome
        })) || [],
        servicos: contrato.servicos?.map(servico => ({
          id_servico_contr_credenciado: contrato.cod_contrato,
          nome: servico.nome,
          valor: Number(servico.valor)
        })) || [],
        exames_toxicos: contrato.exames_toxicos?.map(exame => ({
          id_exame_tox_contr_credenciado: contrato.cod_contrato,
          nome: exame.nome,
          valor: Number(exame.valor)
        })) || []
      };
      // console.log('dados a ser salvos',dadosParaEnviar)
      await api.put('/ravim/update', dadosParaEnviar);

      setSnack(true);
      setSeverity('success');
      setMessage('Contrato atualizado com sucesso!');

      setTimeout(() => {
        history.push('/contratos-credenciado');
      }, 2000);
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setSnack(true);
      setSeverity('error');
      setMessage('Erro ao salvar contrato');
    } finally {
      setLoading((prev) => ({ ...prev, enviando: false }));
    }
  };

  // Renderização condicional
  if (loading.inicial) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Container className={classes.root}>
      <Paper elevation={3} style={{ padding: '24px' }}>
        <Typography variant="h4" gutterBottom>
          Editar Contrato Credenciado - {contrato.nome_contrato || contratoData.nome_contrato}
        </Typography>

        {/* Seção de Dados Básicos */}
        <div className={classes.section}>
          <Typography variant="h6" gutterBottom>
            <InfoIcon color="primary" /> Informações do Contrato
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome do Contrato"
                value={contrato.nome_contrato || contratoData.nome_contrato}
                onChange={(e) =>
                  setContrato({ ...contrato, nome_contrato: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Código do Contrato"
                value={contrato.cod_contrato || contratoData.cod_contrato}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Data de Criação"
                type="date"
                value={contrato.data_criacao || contratoData.data_criacao}
                onChange={(e) =>
                  setContrato({ ...contrato, data_criacao: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Data de Renovação"
                type="date"
                value={contrato.data_renovacao || contratoData.data_renovacao}
                onChange={(e) =>
                  setContrato({ ...contrato, data_renovacao: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </div>

        {/* Seção de Exames */}
        <div className={classes.section}>
          <Typography variant="h6" gutterBottom>
            <InfoIcon color="primary" /> Exames
          </Typography>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={abrirDialogExame}
          >
            Adicionar Exames
          </Button>

          {contrato.exames?.map((exame) => (
            <Paper
              key={exame.id_exames}
              elevation={examesDestacados.includes(exame.id_exames) ? 4 : 2}
              className={
                examesDestacados.includes(exame.id_exames)
                  ? classes.highlight
                  : ''
              }
              style={{ marginTop: '16px', padding: '16px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>{exame.nome}</Typography>
                <Typography>R$ {Number(exame.valor).toFixed(2)}</Typography>
                <IconButton onClick={() => removerExame(exame.nome)}>
                  <DeleteIcon color="error" />
                </IconButton>
              </div>
            </Paper>
          ))}
        </div>

        {/* Seção de Serviços */}
        <div className={classes.section}>
          <Typography variant="h6" gutterBottom>
            <InfoIcon color="primary" /> Serviços
          </Typography>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={abrirDialogServico}
          >
            Adicionar Serviços
          </Button>

          {contrato.servicos?.map((servico) => (
            <Paper
              key={servico.id_servicos}
              elevation={
                servicosDestacados.includes(servico.id_servicos) ? 4 : 2
              }
              className={
                servicosDestacados.includes(servico.id_servicos)
                  ? classes.highlight
                  : ''
              }
              style={{ marginTop: '16px', padding: '16px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>{servico.nome}</Typography>
                <Typography>R$ {Number(servico.valor).toFixed(2)}</Typography>
                <IconButton onClick={() => removerServico(servico.nome)}>
                  <DeleteIcon color="error" />
                </IconButton>
              </div>
            </Paper>
          ))}
        </div>

        {/* Seção de Exames Toxicológicos */}
        <div className={classes.section}>
          <Typography variant="h6" gutterBottom>
            <InfoIcon color="primary" /> Exames Toxicológicos
          </Typography>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={abrirDialogToxico}
          >
            Adicionar Exames Toxicológicos
          </Button>

          {contrato.exames_toxicos?.map((exame) => (
            <Paper
              key={exame.id_exame_tox}
              elevation={
                examesToxicosDestacados.includes(exame.id_exame_tox) ? 4 : 2
              }
              className={
                examesToxicosDestacados.includes(exame.id_exame_tox)
                  ? classes.highlight
                  : ''
              }
              style={{ marginTop: '16px', padding: '16px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>{exame.nome}</Typography>
                <Typography>R$ {Number(exame.valor).toFixed(2)}</Typography>
                <IconButton
                  onClick={() => removerExameToxico(exame.nome)}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </div>
            </Paper>
          ))}
        </div>

        {/* Seção de Riscos */}
        <div className={classes.section}>
          <Typography variant="h6" gutterBottom>
            <InfoIcon color="primary" /> Riscos Ocupacionais
          </Typography>

          <Autocomplete
            multiple
            options={listRiscos}
            getOptionLabel={(option) => `${option.id_risco} - ${option.nome}`}
            value={contrato.riscos || []}
            onChange={handleRiscosChange}
            onOpen={carregarRiscos}
            filterSelectedOptions
            renderInput={(params) => (
              <TextField
                {...params}
                label="Riscos"
                placeholder="Selecione os riscos"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id_risco}
                  label={`${option.nome}`}
                  style={{
                    margin: '4px',
                    backgroundColor: riscosDestacados.includes(option.id_risco)
                      ? '#0D9C5B'
                      : '#e3f2fd',
                    color: riscosDestacados.includes(option.id_risco)
                      ? '#FFF'
                      : '#1976d2',
                    fontWeight: riscosDestacados.includes(option.id_risco)
                      ? 'bold'
                      : 'normal',
                    transition: 'all 0.3s ease'
                  }}
                  onDelete={() => removerRisco(option.nome)}
                />
              ))
            }
          />
        </div>

        {/* Seção de Documentos
        <div className={classes.section}>
          <Typography variant="h6" gutterBottom>
            <InfoIcon color="primary" /> Documentos
          </Typography>

          <Upload />
          <FileList />
        </div> */}

        {/* Botões de Ação */}
        <div className={classes.actionButtons}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push('/contratos-credenciado')}
          >
            Voltar
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={
              loading.enviando ? <CircularProgress size={24} /> : <SaveIcon />
            }
            onClick={salvarContrato}
            disabled={loading.enviando}
          >
            {loading.enviando ? 'Salvando...' : 'Salvar Contrato'}
          </Button>
        </div>
      </Paper>

      {/* Diálogo para Adicionar Exame */}
      <Dialog
        open={dialogExame}
        onClose={() => setDialogExame(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Adicionar Exame
          {loading.exames && (
            <CircularProgress size={24} style={{ marginLeft: 10 }} />
          )}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {loading.exames ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flex={1}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Autocomplete
                options={getExamesDisponiveis()}
                getOptionLabel={(option) => `${option.id_exames} - ${option.nome}`}
                onChange={(_, value) => setSelectedExame(value)}
                filterSelectedOptions
                noOptionsText="O exame já foi adicionado"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selecione o exame"
                    variant="outlined"
                  />
                )}
              />

              <TextField
                label="Valor (R$)"
                type="number"
                fullWidth
                variant="outlined"
                value={valor || ''}
                onChange={(e) => setValor(Number(e.target.value))}
                InputProps={{
                  startAdornment: 'R$'
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogExame(false)}>Cancelar</Button>
          <Button
            onClick={adicionarExame}
            color="primary"
            disabled={!selectedExame || valor === null}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para Adicionar Serviço */}
      <Dialog
        open={dialogServico}
        onClose={() => setDialogServico(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Adicionar Serviço
          {loading.servicos && (
            <CircularProgress size={24} style={{ marginLeft: 10 }} />
          )}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {loading.servicos ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flex={1}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Autocomplete
                options={getServicosDisponiveis()}
                getOptionLabel={(option) => `${option.id_servicos} - ${option.nome}`}
                onChange={(_, value) => setSelectedServico(value)}
                filterSelectedOptions
                noOptionsText="O serviço já foi adicionado"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selecione o serviço"
                    variant="outlined"
                  />
                )}
              />

              <TextField
                label="Valor (R$)"
                type="number"
                fullWidth
                variant="outlined"
                value={valor || ''}
                onChange={(e) => setValor(Number(e.target.value))}
                InputProps={{
                  startAdornment: 'R$'
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogServico(false)}>Cancelar</Button>
          <Button
            onClick={adicionarServico}
            color="primary"
            disabled={!selectedServico || valor === null}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para Adicionar Exame Toxicológico */}
      <Dialog
        open={dialogToxico}
        onClose={() => setDialogToxico(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Adicionar Exame Toxicológico
          {loading.toxicologicos && (
            <CircularProgress size={24} style={{ marginLeft: 10 }} />
          )}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {loading.toxicologicos ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flex={1}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Autocomplete
                options={getExamesToxicosDisponiveis()}
                getOptionLabel={(option) => `${option.id_exame_tox} - ${option.nome}`}
                onChange={(_, value) => setSelectedExameToxico(value)}
                filterSelectedOptions
                noOptionsText="O exame toxicológico já foi adicionado"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selecione o exame toxicológico"
                    variant="outlined"
                  />
                )}
              />

              <TextField
                label="Valor (R$)"
                type="number"
                fullWidth
                variant="outlined"
                value={valor || ''}
                onChange={(e) => setValor(Number(e.target.value))}
                InputProps={{
                  startAdornment: 'R$'
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogToxico(false)}>Cancelar</Button>
          <Button
            onClick={adicionarExameToxico}
            color="primary"
            disabled={!selectedExameToxico || valor === null}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snack}
        autoHideDuration={6000}
        onClose={() => setSnack(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnack(false)} severity={severity}>
          {message}
        </Alert>
      </Snackbar>
    </Container>
  );
}